import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

function EmailLog() {
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState(null);
    const [pending, setPending] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = filter !== "all" ? { status: filter } : undefined;
            const [logData, statsData, pendingData] = await Promise.all([
                api.emailLog(params),
                api.emailLogStats(),
                api.emailLogPending(),
            ]);
            setEntries(logData.entries || []);
            setStats(statsData);
            setPending(pendingData.pending || []);
            setError(null);
        } catch (err) {
            setError(err.message);
        }
    }, [filter]);

    useEffect(() => {
        setLoading(true);
        load().finally(() => setLoading(false));
    }, [load]);

    // Auto-refresh every 30s
    useEffect(() => {
        const interval = setInterval(load, 30000);
        return () => clearInterval(interval);
    }, [load]);

    const fmtTime = (d) => {
        if (!d) return "-";
        const dt = new Date(d);
        return dt.toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
    };

    if (loading) return <LoadingSpinner message="Loading email log..." />;
    if (error) return <div className="card" style={{ padding: 24, color: "#ef4444" }}>Error: {error}</div>;

    return (
        <div>
            <div className="page-header">
                <h2>Email Log</h2>
                <p>Issue email delivery history and retry status</p>
            </div>

            {/* Stats row */}
            {stats && (
                <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                    <div className="card" style={{ padding: "16px 24px", flex: "1 1 150px", minWidth: 150 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#22c55e" }}>{stats.sentToday}</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Sent (24h)</div>
                    </div>
                    <div className="card" style={{ padding: "16px 24px", flex: "1 1 150px", minWidth: 150 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#ef4444" }}>{stats.failedToday}</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Failed (24h)</div>
                    </div>
                    <div className="card" style={{ padding: "16px 24px", flex: "1 1 150px", minWidth: 150 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#f59e0b" }}>{stats.pendingRetry}</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Pending Retry</div>
                    </div>
                    <div className="card" style={{ padding: "16px 24px", flex: "1 1 150px", minWidth: 150 }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#8b5cf6" }}>{stats.maxRetriesReached}</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Max Retries Hit</div>
                    </div>
                </div>
            )}

            {/* Pending retry section */}
            {pending.length > 0 && (
                <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                    <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Queued for Retry</h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: "1px solid #e5e7eb", textAlign: "left" }}>
                                <th style={{ padding: "8px 12px" }}>Issue</th>
                                <th style={{ padding: "8px 12px" }}>Store</th>
                                <th style={{ padding: "8px 12px" }}>Recipient</th>
                                <th style={{ padding: "8px 12px" }}>Retries</th>
                                <th style={{ padding: "8px 12px" }}>Last Error</th>
                                <th style={{ padding: "8px 12px" }}>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pending.map((p) => (
                                <tr key={p.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                    <td style={{ padding: "8px 12px" }}>
                                        <Link to={`/issues?status=open`} style={{ color: "#3b82f6" }}>#{p.id}</Link>
                                        {" "}<span style={{ color: "#6b7280" }}>{p.title}</span>
                                    </td>
                                    <td style={{ padding: "8px 12px" }}>{p.storeName} (#{p.siteId})</td>
                                    <td style={{ padding: "8px 12px" }}>{p.assignedToEmail}</td>
                                    <td style={{ padding: "8px 12px" }}>
                                        <span style={{ background: p.retryCount >= 10 ? "#fee2e2" : "#fef3c7", color: p.retryCount >= 10 ? "#dc2626" : "#d97706", padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                                            {p.retryCount}/10
                                        </span>
                                    </td>
                                    <td style={{ padding: "8px 12px", color: "#ef4444", fontSize: 12, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.lastError || "-"}</td>
                                    <td style={{ padding: "8px 12px", color: "#6b7280" }}>{fmtTime(p.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Filter tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {["all", "sent", "failed"].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        style={{
                            padding: "6px 16px", borderRadius: 6, border: "1px solid #e5e7eb", cursor: "pointer",
                            background: filter === f ? "#1a1d2e" : "#fff",
                            color: filter === f ? "#fff" : "#374151",
                            fontWeight: filter === f ? 600 : 400, fontSize: 13,
                        }}
                    >
                        {f === "all" ? "All" : f === "sent" ? "Sent" : "Failed"}
                    </button>
                ))}
            </div>

            {/* Log table */}
            <div className="card" style={{ padding: 0, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left", background: "#f9fafb" }}>
                            <th style={{ padding: "10px 12px" }}>Time</th>
                            <th style={{ padding: "10px 12px" }}>Issue</th>
                            <th style={{ padding: "10px 12px" }}>Store</th>
                            <th style={{ padding: "10px 12px" }}>Recipient</th>
                            <th style={{ padding: "10px 12px" }}>CC</th>
                            <th style={{ padding: "10px 12px" }}>Status</th>
                            <th style={{ padding: "10px 12px" }}>Attempt</th>
                            <th style={{ padding: "10px 12px" }}>Error</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 ? (
                            <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>No email log entries</td></tr>
                        ) : entries.map((e) => (
                            <tr key={e.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                <td style={{ padding: "8px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>{fmtTime(e.createdAt)}</td>
                                <td style={{ padding: "8px 12px" }}>
                                    {e.issueId ? (
                                        <Link to={`/issues?status=open`} style={{ color: "#3b82f6" }}>#{e.issueId}</Link>
                                    ) : "-"}
                                    {e.issueTitle && <span style={{ color: "#6b7280", marginLeft: 6 }}>{e.issueTitle}</span>}
                                </td>
                                <td style={{ padding: "8px 12px" }}>
                                    {e.storeName ? `${e.storeName} (#${e.siteId})` : "-"}
                                </td>
                                <td style={{ padding: "8px 12px" }}>{e.recipient}</td>
                                <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.cc || "-"}</td>
                                <td style={{ padding: "8px 12px" }}>
                                    <span style={{
                                        background: e.status === "sent" ? "#dcfce7" : "#fee2e2",
                                        color: e.status === "sent" ? "#16a34a" : "#dc2626",
                                        padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                                    }}>
                                        {e.status}
                                    </span>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>{e.attemptNumber}</td>
                                <td style={{ padding: "8px 12px", color: "#ef4444", fontSize: 12, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {e.error || "-"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: 12, fontSize: 12, color: "#9ca3af" }}>
                Showing last 200 entries. Auto-refreshes every 30s.
            </div>
        </div>
    );
}

export default EmailLog;
